import { HttpContext, HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { retry, throwError, timer } from 'rxjs';
import { isApiRequestUrl } from '../utils/api-url.util';
import { MaintenanceStateService } from './maintenance-state.service';

/**
 * Whether a failed API request is retried on transient errors. `null` (default) decides by
 * method: idempotent requests retry, others don't. Set `true` on read-only POSTs (search).
 */
export const RETRY_TRANSIENT = new HttpContextToken<boolean | null>(() => null);

/** Context for a read-only POST that is safe to repeat. */
export const retryTransientContext = () => new HttpContext().set(RETRY_TRANSIENT, true);

/** Backoff between attempts; its length is the number of retries. */
export const TRANSIENT_RETRY_DELAYS_MS = [1000, 2000, 4000];

const TRANSIENT_STATUSES = new Set([0, 429, 502, 503, 504]);
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Retries API requests that failed on the network or a briefly unavailable backend. */
export const transientRetryInterceptor: HttpInterceptorFn = (req, next) => {
  const optIn = req.context.get(RETRY_TRANSIENT) ?? IDEMPOTENT_METHODS.has(req.method);
  if (!optIn || !isApiRequestUrl(req.url)) {
    return next(req);
  }
  const maintenanceState = inject(MaintenanceStateService);
  return next(req).pipe(
    retry({
      count: TRANSIENT_RETRY_DELAYS_MS.length,
      delay: (error: unknown, retryCount: number) => {
        const transient =
          error instanceof HttpErrorResponse
          && TRANSIENT_STATUSES.has(error.status)
          // Maintenance is a deliberate 503; retrying only delays the maintenance screen.
          && !maintenanceState.isMaintenanceError(error);
        return transient
          ? timer(TRANSIENT_RETRY_DELAYS_MS[retryCount - 1])
          : throwError(() => error);
      },
    }),
  );
};
